import { ChevronRight, Minus, Plus, ShoppingBag, Star } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import HtmlContent from '../../components/common/HtmlContent.jsx';
import LoadingState from '../../components/common/LoadingState.jsx';
import Price from '../../components/common/Price.jsx';
import Seo, { SITE_URL, seoText } from '../../components/common/Seo.jsx';
import { useCart } from '../../contexts/CartContext.jsx';
import { assetUrl, storefrontService } from '../../services/storefrontService.js';

export default function ProductPage() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [payload, setPayload] = useState(null);
  const [status, setStatus] = useState('loading');
  const [selection, setSelection] = useState({ sizeId: 0, colorId: 0, quantity: 1, photoIndex: 0 });

  useEffect(() => {
    let active = true;
    setStatus('loading');
    storefrontService.product(id).then((data) => {
      if (!active) return;
      const firstSize = data.product.sizes?.[0]?.id || 0;
      const firstColor = data.product.colors?.[0]?.id || 0;
      setSelection({ sizeId: firstSize, colorId: firstColor, quantity: 1, photoIndex: 0 });
      setPayload(data);
      setStatus('ready');
    }).catch(() => setStatus('error'));
    return () => {
      active = false;
    };
  }, [id]);

  const selectedSize = useMemo(
    () => payload?.product.sizes?.find((size) => size.id === selection.sizeId),
    [payload, selection.sizeId],
  );
  const price = selectedSize?.currentPrice || payload?.product.currentPrice;
  const oldPrice = selectedSize?.oldPrice || payload?.product.oldPrice;

  if (status === 'loading') return <LoadingState label="Loading product" />;
  if (status === 'error' || !payload) return <div className="container page-block"><h1>Product not found</h1></div>;

  const product = payload.product;
  const activePhoto = product.photos[selection.photoIndex] || product.photos[0];
  const available = product.quantity > 0;
  const maxQuantity = Math.max(product.quantity, 1);
  const selectedQuantity = Math.min(selection.quantity, maxQuantity);
  const reviews = payload.reviews || [];
  const averageRating = reviews.length
    ? reviews.reduce((total, review) => total + Number(review.rating || 0), 0) / reviews.length
    : 0;

  function setQuantity(next) {
    setSelection((current) => ({ ...current, quantity: Math.min(Math.max(1, next), maxQuantity) }));
  }

  const productImage = `${SITE_URL}/${String(product.image || '').replace(/^\//, '')}`;
  const productJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Product',
        name: product.name,
        image: productImage,
        description: seoText(product.shortDescription || product.description, 500),
        url: `${SITE_URL}/product/${product.id}`,
        brand: { '@type': 'Brand', name: 'La Bella Trading' },
        offers: {
          '@type': 'Offer',
          url: `${SITE_URL}/product/${product.id}`,
          price: Number(product.currentPrice || 0).toFixed(2),
          priceCurrency: 'CAD',
          availability: available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          itemCondition: 'https://schema.org/NewCondition',
        },
        ...(reviews.length
          ? {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: averageRating.toFixed(1),
                reviewCount: reviews.length,
              },
            }
          : {}),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Products', item: `${SITE_URL}/category/top-category/5` },
          { '@type': 'ListItem', position: 3, name: product.name },
        ],
      },
    ],
  };

  return (
    <>
      <Seo
        title={product.name}
        description={seoText(product.shortDescription || product.description)}
        path={`/product/${product.id}`}
        image={productImage}
        type="product"
        jsonLd={productJsonLd}
      />
      <section className="container product-detail">
        <nav className="breadcrumbs product-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <ChevronRight size={14} />
          <Link to="/category/top-category/5">Products</Link>
          <ChevronRight size={14} />
          <span className="current">{product.name}</span>
        </nav>

        <div className="product-gallery">
          {product.photos.length > 1 ? (
            <div className="thumb-row" aria-label="Product photos">
              {product.photos.map((photo, index) => (
                <button
                  key={`${photo.id}-${index}`}
                  type="button"
                  className={selection.photoIndex === index ? 'selected' : ''}
                  onClick={() => setSelection((current) => ({ ...current, photoIndex: index }))}
                  aria-label={`View photo ${index + 1}`}
                >
                  <img src={assetUrl(photo.image)} alt="" />
                </button>
              ))}
            </div>
          ) : null}

          <div className="product-main-image">
            <img src={assetUrl(activePhoto.image)} alt={product.name} />
          </div>
        </div>

        <div className="product-detail-copy">
          <span className="product-brand">La Bella Trading</span>
          <h1>{product.name}</h1>
          <Price current={price} old={oldPrice} />
          <p>{product.shortDescription}</p>
          <p className={available ? 'stock-line' : 'stock-line stock-line-empty'}>
            {available ? `In stock - ${product.quantity} available` : 'Out of stock'}
          </p>

          {product.sizes.length ? (
            <fieldset className="option-group">
              <legend>Sizes</legend>
              {product.sizes.map((size) => (
                <button
                  className={selection.sizeId === size.id ? 'hex-option selected' : 'hex-option'}
                  key={size.id}
                  type="button"
                  onClick={() => setSelection((current) => ({ ...current, sizeId: size.id }))}
                >
                  <span>{size.name}</span>
                </button>
              ))}
            </fieldset>
          ) : null}

          {product.colors.length ? (
            <fieldset className="option-group swatches">
              <legend>Color</legend>
              {product.colors.map((color) => (
                <button
                  className={selection.colorId === color.id ? 'selected' : ''}
                  key={color.id}
                  type="button"
                  onClick={() => setSelection((current) => ({ ...current, colorId: color.id }))}
                >
                  <span style={{ backgroundColor: color.hex }} />
                  {color.name}
                </button>
              ))}
            </fieldset>
          ) : null}

          <div className="purchase-row">
            <div className="quantity-control">
              <button type="button" disabled={!available || selectedQuantity <= 1} onClick={() => setQuantity(selectedQuantity - 1)} aria-label="Decrease quantity">
                <Minus size={16} />
              </button>
              <span>{selectedQuantity}</span>
              <button type="button" disabled={!available || selectedQuantity >= maxQuantity} onClick={() => setQuantity(selectedQuantity + 1)} aria-label="Increase quantity">
                <Plus size={16} />
              </button>
            </div>
            <button
              className="button product-cart-button"
              type="button"
              disabled={!available}
              onClick={() => addItem({ productId: product.id, quantity: selectedQuantity, sizeId: selection.sizeId, colorId: selection.colorId })}
            >
              <ShoppingBag size={18} /> Add to Cart
            </button>
          </div>
        </div>
      </section>

      <section className="container page-section narrow">
        <div className="detail-head">
          <span className="eyebrow">The details</span>
          <h2>About This Product</h2>
        </div>
        <HtmlContent className="rich-text" html={product.description} />
      </section>

      {reviews.length ? (
        <section className="container page-section narrow product-reviews-section">
          <div className="detail-head">
            <span className="eyebrow">Customer reviews</span>
            <h2>Reviews</h2>
          </div>
          <div className="reviews-panel">
            <div className="reviews-summary">
              <strong>{averageRating.toFixed(1)}</strong>
              <RatingStars rating={Math.round(averageRating)} />
              <span>{reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}</span>
            </div>
            <div className="review-list">
              {reviews.map((review) => (
                <article className="review-card" key={review.id}>
                  <div className="review-card-head">
                    <strong>{review.customerName}</strong>
                    <RatingStars rating={review.rating} />
                  </div>
                  {review.comment ? <p>{review.comment}</p> : null}
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}

function RatingStars({ rating }) {
  return (
    <span className="review-stars" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, index) => (
        <Star
          key={index}
          size={16}
          className={index < Number(rating || 0) ? 'filled' : ''}
          fill={index < Number(rating || 0) ? 'currentColor' : 'none'}
        />
      ))}
    </span>
  );
}
